import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Col, Row, Typography, Form, Tag } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { ActionButton } from '@hdi-seguros/web-components.action-button';
import { Card } from '@hdi-seguros/web-components.card';
import { Grid } from '@hdi-seguros/web-components.grid';

import { CardSkeletons, InputFactory } from '../../../shared/components';
import { PageHeader } from '../../../shared/components/MainLayout/components';
import { PolicyNotFound } from '../../production/InsurancePolicyList/components';
import { UploadFileModal } from './components/UploadFileModal';

import AuthenticationService from '../../../shared/services/authentication';
import MasterService from '../../../shared/services/master';

import { InfoContext } from '../../../shared/contexts';

import { formatDate } from '../../../shared/utils';
import { formattedPrice } from '../../../shared/utils/formattedPrice';
import userHelper from '../../../shared/utils/userHelper';

import { Combo, InputFactoryProp } from '../../../shared/interfaces';

import './index.css';
import { COLORS } from '../../../config/themes/colors';
import { getInvoiceStatusColors } from '../../../shared/utils/getTagsByStates/get-color-states-uploadInvoice';
interface Props {
  title: string;
}

const _renderColumn = (label: string, value: any) => (
  <>
    <Row>
      <Typography.Text type='secondary' style={{ fontSize: 12 }}>
        {label}
      </Typography.Text>
    </Row>
    <Row>
      <Typography.Text>{value}</Typography.Text>
    </Row>
  </>
);

export const UploadInvoice: FC<Props> = ({ title }) => {
  const userInfo = userHelper.getUser();
  const { setTitle } = useContext(InfoContext);
  const gridRef = useRef();

  const [canShowModal, setCanShowModal] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isLoadingCard, setIsLoadingCard] = useState(true);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const [pendingInvoiceFiles, setPendingInvoiceFiles] = useState<any>();
  const [pendingInvoiceList, setPendingInvoiceList] = useState<any>({});
  const [pendingInvoiceSelected, setPendingInvoiceSelected] = useState<any>();
  const [producers, setProducers] = useState<any>([]);
  const [producersCombo, setProducersCombo] = useState<any>([]);
  const [producerSelect, setProducerSelect] = useState({
    codMayorAux: userInfo?.intermediarioSecundario?.codMayorAux,
    nroMayorAux: userInfo?.intermediarioSecundario?.nroMayorAux,
    producer: userInfo?.intermediarioSecundario?.nombreInter,
    cuitInter: userInfo?.intermediarioSecundario?.cuitInter,
    nivelInter: userInfo?.intermediarioSecundario?.nivelInter,
    codigoInter: userInfo?.intermediarioSecundario?.codigoInter,
  });
  const [statesCombo, setStatesCombo] = useState<Combo[]>([]);
  const [form] = Form.useForm();

  const initialStateFilters = useMemo(
    () => ({
      producer: userInfo?.intermediarioSecundario?.codigoInter,
      date: [],
      state: [],
    }),
    /* eslint-disable-next-line */
    []
  );

  const inputsFilter: InputFactoryProp[] = [
    {
      element: 'select',
      placeHolder: 'Productor',
      items: producersCombo,
      allowClear: true,
      name: 'producer',
    },
    {
      element: 'date',
      name: 'date',
    },
    {
      element: 'select',
      isMulti: true,
      placeHolder: 'Estado',
      items: statesCombo,
      name: 'state',
    },
  ];

  useEffect(() => {
    setTitle(title);
    /* eslint-disable-next-line */
  }, [title]);

  useEffect(() => {
    getDetailForm();
    /* eslint-disable-next-line */
  }, []);

  const getDetailForm = async () => {
    try {
      await Promise.all([getComboProducer(), getComboStates()]);
      setIsLoadingFilters(false);
    } catch (error) {}
  };

  const getComboProducer = async () => {
    try {
      const response =
        await AuthenticationService?.getIntermediariesByIntermediary();

      const producers = response?.data?.intermediarios?.intermediario;
      const producerCombo = producers?.map((producer) => {
        const value = producer?.codigoInter;
        const label = `${producer?.codigoInter} - ${producer?.nombreInter}`;

        return { value, label };
      });

      setProducersCombo(producerCombo);
      setProducers(producers);
    } catch (error) {}
  };

  const getComboStates = async () => {
    try {
      const response = await MasterService?.getStatesForCombo();

      setStatesCombo(response.data);
    } catch (error) {}
  };

  const onChangePage = useCallback(
    (page: number, pageSize: number) => {
      const indexOfLastInvoice = page * pageSize;
      const indexOfFirstInvoice = indexOfLastInvoice - pageSize;

      const currentPolicy = pendingInvoiceList?.facturas?.slice(
        indexOfFirstInvoice,
        indexOfLastInvoice
      );
      setPendingInvoiceFiles(currentPolicy || []);

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },
    [pendingInvoiceList]
  );

  useEffect(() => {
    onChangePage(1, 10);
  }, [onChangePage]);

  const findProducerInformation = () => {
    const producerValue = form?.getFieldValue('producer');

    const producer =
      producers.find(
      (producer: { codigoInter: string }) =>
        producer?.codigoInter == producerValue
      ) || userInfo?.intermediarioSecundario;

    const selectedProducer = {
      codMayorAux: producer?.codMayorAux,
      nroMayorAux: producer?.nroMayorAux,
      producer: producer?.nombreInter,
      cuitInter: producer?.cuitInter,
      nivelInter: producer?.nivelInter,
      codigoInter: producer?.codigoInter,
    };

    setProducerSelect(selectedProducer);

    return selectedProducer;
  };

  const onFilterDate = (data: { facturas: any[] }) => {
    const formDate = form?.getFieldValue('date');

    if (formDate?.length > 0) {
      const formattedDates = formDate?.map(
        (date: string | number | Date | dayjs.Dayjs | null | undefined) =>
          dayjs(date).format('YYYY-MM-DD')
      );

      const startDate = dayjs(formattedDates[0]);
      const endDate = dayjs(formattedDates[1]);

      const filteredInvoices = data?.facturas?.filter(
        (invoice: { fechaFactura: string }) => {
          const invoiceDate = dayjs(invoice.fechaFactura, 'YYYY-MM-DD');
          return (
            invoiceDate?.isSame(startDate, 'day') ||
            (invoiceDate?.isAfter(startDate, 'day') &&
              (invoiceDate?.isSame(endDate, 'day') ||
                invoiceDate?.isBefore(endDate, 'day')))
          );
        }
      );

      if (filteredInvoices?.length === 0) {
        setIsEmpty(true);
        return;
      }

      return filteredInvoices;
    }

    return data.facturas;
  };

  useEffect(() => {
    if (pendingInvoiceSelected) {
      (gridRef.current as any).setPage(1);
    }
  }, [pendingInvoiceSelected, setPendingInvoiceSelected]);

  const onFilterState = (data?: any[]) => {
    const formDate = form?.getFieldValue('state');

    if (formDate.length > 0) {
      const findStates = statesCombo?.filter((state) =>
        formDate?.includes(state?.value)
      );

      const filteredStates = data?.filter(
        (invoice: { estadoPresentacion: string }) =>
          findStates?.some(
            (labelState) =>
              labelState?.label?.toLowerCase() ===
              invoice?.estadoPresentacion?.toLowerCase()
          )
      );

      if (filteredStates?.length === 0) {
        setIsEmpty(true);
        return;
      }

      return filteredStates;
    }

    if (data?.length === 0) {
      setIsEmpty(true);
      return;
    }

    return data;
  };

  const onFilter = (data: any) => {
    const filterDate = onFilterDate(data);
    const filterData = onFilterState(filterDate);

    setPendingInvoiceList({ facturas: filterData });

    setTimeout(() => {
      setIsLoadingCard(false);
    }, 0);
  };

  const getPendingInvoices = async () => {
    try {
      setIsLoadingCard(true);
      const producer = await findProducerInformation();
      const response = await MasterService?.getPendingInvoices(producer);
      const data = response.data;

      onFilter(data);
    } catch (error) {}
  };

  const handleOnClick = (invoice: {
    comision: string;
    iva: string;
    totalFactura: string;
    retIva: string;
  }) => {
    setPendingInvoiceSelected(invoice);
    setCanShowModal(true);
  };

  const successUploadingInvoice = () => {
    getPendingInvoices();
  };

  return (
    <>
      {canShowModal && (
        <UploadFileModal
          title='Archivo factura'
          open={canShowModal}
          onClose={() => setCanShowModal(false)}
          pendingInvoiceSelected={pendingInvoiceSelected}
          selectedProducer={producerSelect}
          onSuccess={() => successUploadingInvoice()}
        />
      )}
      <PageHeader />
      <Row align='middle' style={{ marginBottom: 15 }}>
        <InputFactory
          form={form}
          items={inputsFilter}
          onFilter={getPendingInvoices}
          isLoading={isLoadingFilters}
          initialState={initialStateFilters}
        />
      </Row>

      <Grid
        list={pendingInvoiceList?.facturas || []}
        setState={setPendingInvoiceFiles}
        name='Facturas Pendientes'
        ref={gridRef}
      >
        {!isLoadingCard && pendingInvoiceFiles?.length > 0 ? (
          pendingInvoiceFiles?.map(
            (
              invoice: {
                comision: string;
                estadoPresentacion: string;
                fechaFactura: string;
                iva: string;
                moneda: string;
                permiteIngresar: string;
                retIva: string;
                totalFactura: string;
              },
              index: number
            ) => (
              <Row
                wrap
                gutter={[15, 15]}
                key={`Row-${invoice?.totalFactura}-${index}`}
                style={{ marginBottom: 15, width: '100%' }}
              >
                <Card
                  title=''
                  style={{
                    background: COLORS?.neutrals[15],
                    width: '100%',
                  }}
                >
                  <Row gutter={[10, 10]}>
                    <Col
                      style={{ paddingRight: 10 }}
                      xs={{ span: 12 }}
                      sm={{ span: 7 }}
                      md={{ span: 8 }}
                      lg={{ span: 4 }}
                      xl={{ span: 4 }}
                    >
                      {_renderColumn(
                        'Cuenta Corriente',
                        `${producerSelect?.codMayorAux}-${producerSelect?.nroMayorAux}`
                      )}
                    </Col>

                    <Col
                      style={{ paddingRight: 10 }}
                      xs={{ span: 12 }}
                      sm={{ span: 7 }}
                      md={{ span: 8 }}
                      lg={{ span: 4 }}
                      xl={{ span: 4 }}
                    >
                      {_renderColumn(
                        'Productor',
                        `${producerSelect?.producer}`
                      )}
                    </Col>

                    <Col
                      style={{ paddingRight: 10 }}
                      xs={{ span: 12 }}
                      sm={{ span: 7 }}
                      md={{ span: 8 }}
                      lg={{ span: 4 }}
                      xl={{ span: 4 }}
                    >
                      {_renderColumn(
                        'Comisión',
                        `${invoice.moneda} ${formattedPrice(invoice?.comision)}`
                      )}
                    </Col>

                    <Col
                      style={{ paddingRight: 10 }}
                      xs={{ span: 12 }}
                      sm={{ span: 7 }}
                      md={{ span: 8 }}
                      lg={{ span: 4 }}
                      xl={{ span: 4 }}
                    >
                      {_renderColumn(
                        'IVA',
                        `${invoice.moneda} ${formattedPrice(invoice?.iva)}`
                      )}
                    </Col>

                    <Col
                      style={{ paddingRight: 10 }}
                      xs={{ span: 12 }}
                      sm={{ span: 7 }}
                      md={{ span: 8 }}
                      lg={{ span: 4 }}
                      xl={{ span: 4 }}
                    >
                      {_renderColumn(
                        'Total factura',
                        `${invoice.moneda}
                      ${formattedPrice(invoice?.totalFactura)}`
                      )}
                    </Col>

                    <Col
                      style={{ paddingRight: 10 }}
                      xs={{ span: 12 }}
                      sm={{ span: 7 }}
                      md={{ span: 8 }}
                      lg={{ span: 4 }}
                      xl={{ span: 4 }}
                    >
                      {_renderColumn(
                        'Retención IVA',
                        `${invoice.moneda}
                      ${formattedPrice(invoice?.retIva)}`
                      )}
                    </Col>

                    <Col
                      style={{ paddingRight: 10 }}
                      xs={{ span: 12 }}
                      sm={{ span: 7 }}
                      md={{ span: 8 }}
                      lg={{ span: 4 }}
                      xl={{ span: 4 }}
                    >
                      {_renderColumn(
                        'Fecha Factura',
                        formatDate(invoice?.fechaFactura)
                      )}
                    </Col>

                    <Col
                      style={{ paddingRight: 10 }}
                      xs={{ span: 12 }}
                      sm={{ span: 7 }}
                      md={{ span: 8 }}
                      lg={{ span: 4 }}
                      xl={{ span: 4 }}
                    >
                      {_renderColumn(
                        'Estado',
                        <Tag color={getInvoiceStatusColors(invoice?.estadoPresentacion)}>{invoice?.estadoPresentacion}</Tag>
                      )}
                    </Col>

                    <Col
                      style={{ paddingRight: 10 }}
                      xs={{ span: 12 }}
                      sm={{ span: 7 }}
                      md={{ span: 8 }}
                      lg={{ span: 4 }}
                      xl={{ span: 4 }}
                    >
                      <Row>
                        <Typography.Text
                          type='secondary'
                          style={{ fontSize: 12 }}
                        >
                          Archivo factura
                        </Typography.Text>
                      </Row>
                      <Row>
                        {invoice.permiteIngresar === 'S' ? (
                          <ActionButton
                            type='secondary'
                            className="btn-accent-outline-barbuss"
                            label='Subir'
                            onClick={() => handleOnClick(invoice)}
                            icon={<UploadOutlined />}
                          />
                        ) : (
                          <Typography.Text>
                            La factura ya fue presentada
                          </Typography.Text>
                        )}
                      </Row>
                    </Col>
                  </Row>
                </Card>
              </Row>
            )
          )
        ) : !isLoadingCard && isEmpty ? (
          <Row style={{ width: '100%' }} gutter={[15, 15]}>
            <PolicyNotFound />
          </Row>
        ) : (
          <Row style={{ width: '100%' }} gutter={[15, 15]}>
            <CardSkeletons />
          </Row>
        )}
      </Grid>
    </>
  );
};
